import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import DatePickerActionDates from './DatePickerActionDates';
import DatePickerTooltip from '../tooltips/DatePickerTooltip';
import dayjs from '../../config/dayjs';
import SingletonOpenManager from '../../helpers/singleton-manager';
import useSingletonOpenComponent from '../../hooks/useSingletonOpenComponent';
import { getValidDate } from '../../helpers/date-to-pay-restrictions';
import DEFAULT_PICKERSDAY_CLASS_NAME from '../../theme/constants/pickers-day';
import { useCartDateDisableCheck, useUpcomingHolidayDates } from '../../hooks/useUpcomingHolidays';

const datePickerManager = new SingletonOpenManager();

const OnboardingDatePicker = ({
  value,
  onChange,
  onRecommendedPaymentTerm,
  onChangeAll,
  debtorAveragePaymentDays,
  minDate,
  maxDate,
  disabled,
  shouldDisableWeekend,
  ButtonComponent,
  stackStyles,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const holidayDates = useUpcomingHolidayDates();
  const cartShouldDisableDate = useCartDateDisableCheck();
  const disableDateFn = shouldDisableWeekend || cartShouldDisableDate;
  const datePickerId = useSingletonOpenComponent(datePickerManager, setIsOpen);

  const validatedValue = useMemo(() => {
    if (value) {
      return getValidDate(value, minDate, maxDate, holidayDates);
    }
    return '';
  }, [value, minDate, maxDate, holidayDates]);

  const handleRenderDay = (day, _selectedDates, pickersDayProps) => (
    <DatePickerTooltip
      day={day}
      pickersDayProps={pickersDayProps}
      minDate={minDate}
      maxDate={maxDate}
      className={DEFAULT_PICKERSDAY_CLASS_NAME}
    />
  );

  const handleOpen = () => {
    datePickerManager.open(datePickerId);
  };

  const handleClose = () => {
    datePickerManager.close(datePickerId);
  };

  return (
    <DatePicker
      disabled={disabled}
      value={validatedValue || ''}
      open={isOpen || false}
      onOpen={handleOpen}
      onClose={handleClose}
      renderInput={(params) => (
        <Stack {...stackStyles}>
          <TextField
            {...params}
            sx={{ visibility: 'hidden', height: 0, width: 0, minHeight: 0, minWidth: 0 }}
            inputProps={{
              ...params.inputProps,
              placeholder: 'dd-mm-aaaa',
              sx: { visibility: 'hidden', height: 0, width: 0, minHeight: 0, minWidth: 0 },
              'data-hj-allow': '',
              'data-hj-datepicker': '',
              value: value ? params.inputProps.value : '',
              readOnly: true,
            }}
          />
          <ButtonComponent
            {...params}
            setOpen={(newOpen) => {
              if (newOpen) {
                datePickerManager.open(datePickerId);
              } else {
                datePickerManager.close(datePickerId);
              }
            }}
            value={value}
          />
        </Stack>
      )}
      onChange={onChange}
      closeOnSelect={false}
      minDate={minDate}
      defaultCalendarMonth={minDate}
      maxDate={maxDate}
      components={{
        ActionBar: DatePickerActionDates,
      }}
      componentsProps={{
        actionBar: {
          onAllActions: onChangeAll,
          onRecommendedPaymentTerm,
          closeDatePicker: handleClose,
          handleChange: onChange,
          debtorAveragePaymentDays,
          disableWeekend: true,
          holidayDates,
          showAllActionsButton: false,
        },
      }}
      shouldDisableDate={disableDateFn}
      renderDay={handleRenderDay}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: ({ placement }) => {
                if (placement.startsWith('top')) return [0, 20];
                return [0, 20];
              },
            },
          },
        ],
      }}
    />
  );
};

OnboardingDatePicker.propTypes = {
  debtorAveragePaymentDays: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.instanceOf(dayjs), PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  onChangeAll: PropTypes.func,
  onRecommendedPaymentTerm: PropTypes.func,
  minDate: PropTypes.instanceOf(dayjs),
  maxDate: PropTypes.instanceOf(dayjs),
  disabled: PropTypes.bool,
  shouldDisableWeekend: PropTypes.func,
  ButtonComponent: PropTypes.elementType.isRequired,
  stackStyles: PropTypes.shape({
  }),

};

OnboardingDatePicker.defaultProps = {
  debtorAveragePaymentDays: null,
  value: '',
  onChangeAll: null,
  onRecommendedPaymentTerm: null,
  minDate: dayjs().subtract(180, 'days'),
  maxDate: dayjs().add(150, 'days'),
  disabled: false,
  shouldDisableWeekend: null,
  stackStyles: { height: '80%', justifyContent: 'center', width: '100%' },

};

export default OnboardingDatePicker;
